import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';

const NAV_ITEMS = [
  { label: 'nav.about', to: '/about' },
  { label: 'nav.business', to: '/business' },
  { label: 'nav.stats', to: '/stats' },
  { label: 'nav.news', to: '/news' },
];

export default function ElegantHeader() {
  const { t } = useTranslation();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const isHome = location.pathname === '/';
  const isTransparent = isHome && !scrolled;

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-500"
      style={{
        background: isTransparent ? 'transparent' : 'rgba(250, 249, 246, 0.95)',
        backdropFilter: isTransparent ? 'none' : 'blur(12px)',
        borderBottom: isTransparent ? 'none' : '1px solid rgba(212, 207, 196, 0.4)',
        height: '80px',
      }}
    >
      <div className="flex items-center justify-between px-6 md:px-10 h-full max-w-[1400px] mx-auto">
        {/* Logo */}
        <Link to="/" className="cursor-pointer flex items-center shrink-0">
          <img
            src="https://storage.readdy-site.link/project_files/b7870d10-3857-48a7-b51c-5ccdd10677e8/15e7caab-e426-467f-932e-5dd53158ad6b_TradFit.png?v=5d09026c2de21274a37f01665a04ce12"
            alt="TradFit株式会社"
            className="h-10 w-auto object-contain transition-all duration-500"
            style={{
              filter: isTransparent
                ? 'brightness(0) invert(1)'
                : 'none',
            }}
          />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center" style={{ gap: '36px' }}>
          {NAV_ITEMS.map((item) => {
            const isActive = location.pathname === item.to;
            return (
              <Link
                key={item.to}
                to={item.to}
                className="relative whitespace-nowrap cursor-pointer no-underline transition-colors duration-300 text-base tracking-wide"
                style={{
                  fontFamily: "'Noto Sans JP', 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif",
                  fontWeight: isActive ? 600 : 400,
                  color: isActive
                    ? '#C9A227'
                    : isTransparent
                      ? 'rgba(255,255,255,0.85)'
                      : '#3E3B34',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLAnchorElement).style.color = '#C9A227';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLAnchorElement).style.color = isActive
                    ? '#C9A227'
                    : isTransparent
                      ? 'rgba(255,255,255,0.85)'
                      : '#3E3B34';
                }}
              >
                {t(item.label)}
                {isActive && (
                  <span
                    className="absolute -bottom-1 left-0 right-0 h-px"
                    style={{ background: '#C9A227' }}
                  />
                )}
              </Link>
            );
          })}
          <LanguageSwitcher scrolled={!isTransparent} />
          <Link
            to="/contact"
            className="whitespace-nowrap cursor-pointer no-underline text-base tracking-wider transition-all duration-300"
            style={{
              fontFamily: "'Noto Serif JP', serif",
              fontWeight: 500,
              padding: '10px 28px',
              border: '1px solid',
              borderColor: isTransparent ? 'rgba(255,255,255,0.6)' : '#C9A227',
              color: isTransparent ? '#FFFFFF' : '#C9A227',
              background: isTransparent ? 'transparent' : 'transparent',
            }}
            onMouseEnter={(e) => {
              const el = e.currentTarget as HTMLAnchorElement;
              el.style.background = '#C9A227';
              el.style.color = '#FFFFFF';
              el.style.borderColor = '#C9A227';
            }}
            onMouseLeave={(e) => {
              const el = e.currentTarget as HTMLAnchorElement;
              el.style.background = 'transparent';
              el.style.color = isTransparent ? '#FFFFFF' : '#C9A227';
              el.style.borderColor = isTransparent ? 'rgba(255,255,255,0.6)' : '#C9A227';
            }}
          >
            {t('nav.contact')}
          </Link>
        </nav>

        {/* Mobile Hamburger */}
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className="md:hidden flex flex-col justify-center cursor-pointer"
          style={{ width: '24px', height: '24px', gap: '5px' }}
          aria-label="Menu"
        >
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="block transition-all duration-300"
              style={{
                width: '24px',
                height: '1.5px',
                background: isTransparent ? '#FFFFFF' : '#3E3B34',
                transform:
                  menuOpen && i === 0
                    ? 'rotate(45deg) translate(5px,5px)'
                    : menuOpen && i === 1
                      ? 'scaleX(0)'
                      : menuOpen && i === 2
                        ? 'rotate(-45deg) translate(5px,-5px)'
                        : 'none',
                opacity: menuOpen && i === 1 ? 0 : 1,
              }}
            />
          ))}
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div
          className="md:hidden w-full"
          style={{
            background: 'rgba(250, 249, 246, 0.98)',
            backdropFilter: 'blur(12px)',
            borderBottom: '1px solid rgba(212, 207, 196, 0.4)',
          }}
        >
          {[
            ...NAV_ITEMS,
            { label: 'nav.contact', to: '/contact' },
          ].map((item) => (
            <Link
              key={item.to}
              to={item.to}
              onClick={() => setMenuOpen(false)}
              className="block no-underline cursor-pointer transition-colors duration-200"
              style={{
                fontFamily: "'Noto Sans JP', 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif",
                fontSize: '17px',
                color: location.pathname === item.to ? '#C9A227' : '#3E3B34',
                padding: '12px 24px',
                borderBottom: '1px solid rgba(212, 207, 196, 0.3)',
                fontWeight: location.pathname === item.to ? 600 : 400,
              }}
            >
              {t(item.label)}
            </Link>
          ))}
          <div className="px-6 py-3">
            <LanguageSwitcher scrolled={true} />
          </div>
        </div>
      )}
    </header>
  );
}